import { CrudFilters, HttpError, useCreate, useOne, useTable } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { IParticipantIdsData, ISportParticipantItem } from "interfaces";
import { Filter, ParticipantCard, ParticipantTable } from "./components";
import Pagination from "components/pagination";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { DropdownMenu } from "components/dropdownMenu";
import Button from "components/button";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/outline";
import { ConfirmModal } from "components/confirmModal";
import { get, isEmpty } from "lodash";
import toast from "react-hot-toast";
import Input from "components/input";
import { Link } from "react-router-dom";
import { isHaveActionPermission } from "utills/helpers";

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  title: "",
  submit: () => {}
}

export const SportParticipantList: React.FC = () => {
  const [participantIds, setParticipantIds] = useState<string[] | null>([]);
  const [confirmModalConfig, setConfirmModalConfig] = useState(INITIAL_ACTION_MODAL_CONFIG);
  const [exportId, setExportId] = useState<string | null>();

  const { mutate: postActionMutation, isLoading: createMutationIsLoading } = useCreate();

  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<ISportParticipantItem>({
    resource: "participant",
    syncWithLocation: true,
    filters: {
      initial: [
        {
          field: "gte",
          operator: "eq",
          value: moment().format("YYYY-MM-DD"),
        },
        {
          field: "lte",
          operator: "eq",
          value: moment().format("YYYY-MM-DD"),
        }
      ]
    },
    pagination: {
      mode: "off"
    },
    meta: {
      resource: "data",
    },
  });

  const { isFetching: isFetchingParticipantIdsData, refetch: fetchParticipantIds } = useOne<IParticipantIdsData, HttpError>({
    resource: "participant/ids",
    id: "",
    meta: {
      resource: "data",
      filters: filters,
    },
    queryOptions: {
      enabled: false
    } as any,
    successNotification: (data, values, resource) => {
      setParticipantIds(data?.data?.participant_ids ?? null);
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    }
  });
  
  const { refetch: checkStatus } = useOne({
    resource: "export/result",
    id: exportId ?? "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: false,
    } as any,
    errorNotification: (data, values, resource) => {
      setExportId(null);
      return {
        message: `Export Error`,
        description: "Export Error",
        type: "error",
      }
    },
  });

  useEffect(() => {
    let intervalId: any;

    if (exportId) {
      intervalId = setInterval(() => {
        checkStatus().then((data) => {
          const isSuccess = get(data, "data.data.is_success", false);
          if (isSuccess) {
            setExportId(null);
            window.location.href = get(data, "data.data.file_link", "#");
          }
        });
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [exportId, checkStatus]);

  const _checkStatus = (id: string) => {
    setExportId(id);
  }

  const _onCloseConfirmModal = () => {
    setConfirmModalConfig({
      ...INITIAL_ACTION_MODAL_CONFIG,
      title: confirmModalConfig.title
    });
  }

  const _sendQRCode = (ids: string[]) => {
    postActionMutation({
      resource: "participant/send-qr-code",
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        participant_ids: ids
      },
    },
    {
      onSuccess: () => {
        toast.success("Send QR checkin onsite successfully");
      },
    });
  }

  const _sendUniqueLink = (ids: string[]) => {
    postActionMutation({
      resource: "participant/send-unique-link",
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        participant_ids: ids
      },
    },
    {
      onSuccess: () => {
        toast.success("Send unique URL email successfully");
      },
    });
  }


  const _syncToPickup = (ids: string[]) => {
    postActionMutation({
      resource: "participant/sync-to-pickup",
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        participant_ids: ids
      },
    },
    {
      onSuccess: () => {
        toast.success("Sync to pickup successfully");
      },
    });
  }

  const _clearBIB = (ids: string[]) => {
    postActionMutation({
      resource: "participant/clear-bib",
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        participant_ids: ids
      },
    },
    {
      onSuccess: () => {
        toast.success("Clear BIB successfully");
        tableQuery.refetch();
      },
    });
  }

  const _sendPaymentSuccessAttendant = (ids: string[]) => {
    postActionMutation({
      resource: "participant/send-payment-success-attendant",
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        participant_ids: ids
      },
    },
    {
      onSuccess: () => {
        toast.success("Send a successful registration email successfully");
      },
    });
  }

  const _toggleActive = (id: string, isActive: boolean) => {
    postActionMutation({
      resource: "participant/change-status",
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        participant_ids: [id],
        is_active: isActive
      },
    },
    {
      onSuccess: () => {
        toast.success("Change participant status successfully");
        tableQuery.refetch();
      },
    });
  }

  const _exportParticipants = (ids: string[]) => {
    postActionMutation({
      resource: "export/sport-participants",
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        participant_ids: ids
      },
    },
    {
      onSuccess: (res) => {
        const exportId = get(res, "data.data.export_id", "");
        _checkStatus(exportId)
      },
    });
  }

  const _exportParticipantsAddon = (ids: string[]) => {
    postActionMutation({
      resource: "export/sport-participants-addon",
      meta: {
        ignoreResourceWrapper: true
      },
      values: {
        participant_ids: ids
      },
    },
    {
      onSuccess: (res) => {
        const exportId = get(res, "data.data.export_id", "");
        _checkStatus(exportId)
      },
    });
  }

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      event_id: logicalFilters.find((item) => item.field === "event_id")?.value || "",
      category: logicalFilters.find((item) => item.field === "category")?.value || "",
      ewaiver_status: logicalFilters.find((item) => item.field === "ewaiver_status")?.value || "",
      lte: logicalFilters.find((item) => item.field === "lte")?.value || "",
      gte: logicalFilters.find((item) => item.field === "gte")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      source: logicalFilters.find((item) => item.field === "source")?.value || "",
    };
  }, [filters]);

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }

  const _undoCheckParticipantId = (id: string) => {
    const udpatedParticipantIds = [...participantIds ?? []].filter(participantId => participantId !== id);
    setParticipantIds(udpatedParticipantIds);
  }

  const _checkParticipantId = (id: string) => {
    if ((participantIds ?? []).includes(id)) return;
    const udpatedParticipantIds = [...participantIds ?? []];
    udpatedParticipantIds.push(id);
    setParticipantIds(udpatedParticipantIds);
  }

  const _renderActionButtons = (isMobile: boolean) => {
    return !isEmpty(participantIds) ? (
      <div className={`shrink-0 ${isMobile ? "block sm:hidden" : "hidden sm:block"}`}>
        <DropdownMenu
          position="bottom"
          label={
            <Button className="w-fit bg-white rounded-md text-brand-500 font-bold border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85">
              More actions
              <ChevronDownIcon className="ml-2 size-4 text-brand-500" />
            </Button>
          }
          items={[
          ...(isHaveActionPermission("sports", "participants", "export")
            ? [
                {
                  label: "Download Participant",
                  onClick: () =>  setConfirmModalConfig({
                    open: true,
                    title: `Do you want to perform a 'Download Attendant' for ${(participantIds ?? [])?.length} participants?`,
                    submit: () => _exportParticipants(participantIds ?? [])
                  })
                },
                {
                  label: "Download Participant Addon",
                  onClick: () =>  setConfirmModalConfig({
                    open: true,
                    title: `Do you want to perform a 'Download Participant Addon' for ${(participantIds ?? [])?.length} participants?`,
                    submit: () => _exportParticipantsAddon(participantIds ?? [])
                  })
                },
              ]
            : []),
            ...(isHaveActionPermission("sports", "participants", "edit")
            ? [
                {
                  label: "Sync participant to Pickup",
                  onClick: () =>  setConfirmModalConfig({
                    open: true,
                    title: `Do you want to perform a 'Sync participant to Pickup' for ${(participantIds ?? [])?.length} participants?`,
                    submit: () => _syncToPickup(participantIds ?? [])
                  })
                },
                {
                  label: "Send unique URL email",
                  onClick: () =>  setConfirmModalConfig({
                    open: true,
                    title: `Do you want to perform a 'Send unique URL email' for ${(participantIds ?? [])?.length} participants?`,
                    submit: () => _sendUniqueLink(participantIds ?? [])
                  })
                },
                {
                  label: "Send QR checkin onsite",
                  onClick: () =>  setConfirmModalConfig({
                    open: true,
                    title: `Do you want to perform a 'Send QR checkin onsite' for ${(participantIds ?? [])?.length} participants?`,
                    submit: () => _sendQRCode(participantIds ?? [])
                  })
                },
                {
                  label: "Send a successful registration email",
                  onClick: () =>  setConfirmModalConfig({
                    open: true,
                    title: `Do you want to perform a 'Send a successful registration email' for ${(participantIds ?? [])?.length} participants?`,
                    submit: () => _sendPaymentSuccessAttendant(participantIds ?? [])
                  })
                },
                {
                  label: "Clear BIB",
                  onClick: () =>  setConfirmModalConfig({
                    open: true,
                    title: `Do you want to perform a 'Clear BIB' for ${(participantIds ?? [])?.length} participants?`,
                    submit: () => _clearBIB(participantIds ?? [])
                  })
                },
              ]
            : []),
          ]}
        />
      </div>
    ) : <></>
  }

  if (tableQuery.isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="wrapper">
      <Breadcrumbs
        additionalLabel={
          <div className="flex items-center gap-x-2.5">
            {isHaveActionPermission("sports", "participants", "export") ||
            isHaveActionPermission("sports", "participants", "edit")
              ? _renderActionButtons(false)
              : null}
            {isHaveActionPermission("sports", "participants", "create") ? (
              <Link to={"/participants/sport/mass-import"}>
                <Button>
                  <span className="sr-only">Create/Mass update</span>
                  <PlusIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">
                    Create/Mass update
                  </span>
                </Button>
              </Link>
            ) : <></>}
          </div>
        }
      />
      <div className="box-container">
        <Filter currentFilterValues={currentFilterValues} setFilters={(data: CrudFilters) => {
          setFilters(data);
          setParticipantIds([]);
        }} />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length &&
        !tableQuery.isFetching && (
          <div className="text-center">
            No data
          </div>
        )}

        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <div className="w-full flex items-center justify-between mb-4">
                <div>
                  <Input
                    type="checkbox"
                    label="Select All"
                    checked={!isEmpty(participantIds)}
                    disabled={
                      !isHaveActionPermission(
                        "sports",
                        "participants",
                        "export"
                      ) &&
                      !isHaveActionPermission("sports", "participants", "edit")
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.checked) {
                        fetchParticipantIds();
                      } else {
                        setParticipantIds([]);
                      }
                    }}
                  />
                </div>
                {isHaveActionPermission("sports", "participants", "export") ||
                isHaveActionPermission("sports", "participants", "edit") ? (
                  <div>{_renderActionButtons(true)}</div>
                ) : (
                  <></>
                )}
              </div>
              <ParticipantCard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                participantIds={participantIds}
                _undoCheckParticipantId={_undoCheckParticipantId}
                _checkParticipantId={_checkParticipantId}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <ParticipantTable 
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                participantIds={participantIds}
                _undoCheckParticipantId={_undoCheckParticipantId}
                _checkParticipantId={_checkParticipantId}
                setParticipantIds={setParticipantIds}
                fetchParticipantIds={fetchParticipantIds}
                _toggleActive={_toggleActive}
              />
            </div>
            {tableQuery?.data?.paging ? (
              <div className="mt-6">
                <Pagination
                  currentPage={tableQuery?.data?.paging.current_page}
                  totalPage={tableQuery?.data?.paging.total_page}
                  total={{
                    totalItems: tableQuery?.data?.paging?.total_item ?? 0,
                    total: tableQuery?.data?.paging.total
                  }}
                  onChange={_handlePageChanged}
                />
              </div>
            ) : <></>}
          </>
        ) : <></>}
      </div>
      {(tableQuery.isFetching && tableQuery.isPreviousData) || isFetchingParticipantIdsData || createMutationIsLoading || exportId ? <FullScreenLoading /> : <></>}
      <ConfirmModal 
        key="confirm-action"
        title={confirmModalConfig.title}
        content=""
        open={confirmModalConfig.open}
        onConfirm={confirmModalConfig.submit}
        onClose={_onCloseConfirmModal}
      />
    </div>
  )
};
